import React, { FC } from 'react';
import { useFormikContext } from 'formik';

import {
  FormActionFooter,
  Row,
  Column,
  FormInput,
  FormNavPrompt,
  HRule,
} from 'Shared/components';
import { STATE_OPTIONS } from 'Shared/constants/states';
import { isMissing, stripLeadingZeros } from 'Shared/utils';
import { useAuth0 } from 'Auth';
import { UserRolesWrapper } from 'Admin/Users/components/UserRoles';
import { PartnerType } from 'Admin/Partners';
import { UserType } from 'Admin/Users/types';
import { NpnStatus } from 'Admin/components/NpnStatus';
import { NpnStatusType } from 'Admin/components/NpnStatus/NpnStatus';
import { NpnExternalLink } from 'Admin/components';

import styles from './styles.module.css';
import { canUpdatePartnerAddressPolicy } from 'Admin/authPolicies';

export type UserFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  suffix: string;
  originalNationalProducerNumber?: string; // used to compare initial value in validation
  nationalProducerNumber: string;
  npnStatus?: NpnStatusType; // not present on create
  phoneNumber: string;
  address: {
    lineOne: string;
    lineTwo: string;
    city: string;
    zipCode: string;
    state: string;
  };
  roles: string[];
  serverValidationErrors: {
    nationalProducerNumber?: Array<string>;
    email?: Array<string>;
  };
};

type UserFormType = {
  type: 'create' | 'edit';
  disabled?: boolean;
  hasValidationError?: boolean;
  currentPartner?: PartnerType;
  currentUser?: Partial<UserType>;
  footerError?: string;
  footerButtonText?: string;
  allowNavigatingAway?: boolean;
};

export const UserForm: FC<UserFormType> = ({
  disabled,
  hasValidationError,
  currentPartner,
  currentUser,
  footerError,
  footerButtonText,
  type,
  allowNavigatingAway = false,
}) => {
  const {
    values,
    errors,
    handleChange,
    dirty,
    handleSubmit,
    handleBlur,
    isSubmitting,
    touched,
    setTouched,
    setFieldValue,
    setValues,
  } = useFormikContext<UserFormValues>();

  const { user } = useAuth0();

  const canEditAddress = canUpdatePartnerAddressPolicy(user!.effectiveGrants);

  const isUserWithPartner = !!currentPartner;

  const npnIsEditable =
    isMissing(currentUser?.licensedAgent?.nationalProducerNumber) || // No value yet
    values.npnStatus === 'invalid' || // npn is invalid
    user?.isCarrierUser ||
    user?.isSystemUser; // internal admins can edit regardless of status

  const displayNPNExternalLink =
    npnIsEditable && values.npnStatus !== 'success';

  return (
    <form onSubmit={handleSubmit}>
      <FormNavPrompt when={dirty && !allowNavigatingAway} />
      <h3>User Information</h3>
      <Row>
        <Column className={styles.columnLarge}>
          <FormInput
            name="firstName"
            value={values.firstName}
            label="First Name"
            onBlur={handleBlur}
            variant="text"
            onChange={handleChange}
            disabled={disabled}
            errorMessage={
              type === 'edit' || touched.firstName
                ? errors.firstName
                : undefined
            }
          />
        </Column>
        <Column className={styles.columnLarge}>
          <FormInput
            name="lastName"
            value={values.lastName}
            label="Last Name"
            onBlur={handleBlur}
            variant="text"
            onChange={handleChange}
            disabled={disabled}
            errorMessage={
              type === 'edit' || touched.lastName ? errors.lastName : undefined
            }
          />
        </Column>
        <Column className={styles.columnSmall}>
          <FormInput
            name="suffix"
            value={values.suffix}
            label="Suffix"
            onBlur={handleBlur}
            variant="text"
            onChange={handleChange}
            disabled={disabled}
            errorMessage={
              type === 'edit' || touched.suffix ? errors.suffix : undefined
            }
          />
        </Column>
      </Row>

      <HRule size="lg" />

      <h3>{!isUserWithPartner && 'System '}Roles</h3>
      <p>NPN will be required to quote, bind, and service policies</p>
      <Row>
        <Column>
          <UserRolesWrapper
            disabled={disabled}
            currentUser={currentUser}
            currentPartner={currentPartner}
          />
        </Column>
      </Row>
      <Row>
        <Column className={styles.columnLarge}>
          <FormInput
            disabled={!npnIsEditable || disabled}
            variant="text"
            name="nationalProducerNumber"
            tooltip={
              npnIsEditable
                ? `Add the user’s NPN so they can quote or complete premium-impacting endorsements. Once verified, this value can not be changed without calling a Clearcover advocate.`
                : 'NPN cannot be changed. If NPN is incorrect, please email appointmentrequests@clearcover.com for support.'
            }
            onChange={(e: React.FocusEvent<HTMLInputElement>) => {
              setTouched({ ...touched, nationalProducerNumber: true });
              setValues({
                ...values,
                nationalProducerNumber: stripLeadingZeros(e.target.value) || '',
                serverValidationErrors: {
                  ...values.serverValidationErrors,
                  nationalProducerNumber: undefined,
                },
              });
            }}
            onBlur={handleBlur}
            value={values.nationalProducerNumber}
            label="User National Producer Number"
            errorMessage={
              type === 'edit' || touched.nationalProducerNumber
                ? errors.nationalProducerNumber
                : undefined
            }
          />
          <div className={styles.npnPostLabels}>
            {displayNPNExternalLink && (
              <NpnExternalLink
                moveToTop={
                  !errors.nationalProducerNumber &&
                  values.npnStatus !== 'invalid'
                }
              />
            )}

            {!errors.nationalProducerNumber &&
              values.npnStatus !== 'invalid' && (
                <NpnStatus status={values.npnStatus} />
              )}
          </div>
        </Column>
        <Column />
      </Row>

      <HRule size="lg" />
      <h3>Contact Information</h3>
      <Row>
        <Column className={styles.columnLarge}>
          <FormInput
            name="email"
            value={values.email}
            label="Email"
            variant="text"
            disabled={disabled}
            onChange={(e: React.FocusEvent<HTMLInputElement>) => {
              setTouched({ ...touched, email: true });
              setValues({
                ...values,
                email: e.target.value,
                serverValidationErrors: {
                  ...values.serverValidationErrors,
                  email: undefined,
                },
              });
            }}
            onBlur={handleBlur}
            errorMessage={
              type === 'edit' || touched.email ? errors.email : undefined
            }
          />
        </Column>
        <Column className={styles.columnLarge}>
          <FormInput
            name="phoneNumber"
            value={values.phoneNumber || ''}
            label="Phone"
            onBlur={handleBlur}
            variant="text"
            onChange={handleChange}
            disabled={disabled}
            errorMessage={
              type === 'edit' || touched.phoneNumber
                ? errors.phoneNumber
                : undefined
            }
          />
        </Column>
        <Column className={styles.columnSmall} />
      </Row>
      <Row>
        <Column className={styles.columnLarge}>
          <FormInput
            name="address.lineOne"
            value={values.address.lineOne}
            label="Address"
            onBlur={handleBlur}
            variant="text"
            onChange={handleChange}
            disabled={disabled || !canEditAddress}
          />
        </Column>
        <Column className={styles.columnLarge}>
          <FormInput
            name="address.lineTwo"
            value={values.address.lineTwo}
            label="Address 2"
            onBlur={handleBlur}
            variant="text"
            onChange={handleChange}
            disabled={disabled || !canEditAddress}
          />
        </Column>
        <Column className={styles.columnSmall} />
      </Row>
      <Row>
        <Column className={styles.columnLarge}>
          <FormInput
            name="address.city"
            label="City"
            onBlur={handleBlur}
            variant="text"
            onChange={handleChange}
            value={values.address.city}
            disabled={disabled || !canEditAddress}
          />
        </Column>
        <Column className={styles.columnLarge}>
          <FormInput
            variant="selectAutocomplete"
            options={STATE_OPTIONS}
            value={values.address.state}
            onChange={(value: string) => setFieldValue('address.state', value)}
            name="address.state"
            label="State"
            disabled={disabled || !canEditAddress}
          />
        </Column>
        <Column className={styles.columnSmall}>
          <FormInput
            name="address.zipCode"
            value={values.address.zipCode || ''}
            label="Zip Code"
            onBlur={handleBlur}
            variant="text"
            onChange={handleChange}
            disabled={disabled || !canEditAddress}
            errorMessage={
              type === 'edit' || (touched.address && touched.address.zipCode)
                ? errors.address && errors.address.zipCode
                : undefined
            }
          />
        </Column>
      </Row>

      <FormActionFooter
        error={footerError}
        disabled={Boolean(!dirty || hasValidationError)}
        isSubmitting={isSubmitting}
        buttonText={footerButtonText}
      />
    </form>
  );
};
